'use client'
import AuthModal from '@/app/components/auth-modal'
import { getUserOnClient } from '@/util/user'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { SWRConfig } from 'swr'
import { ILoggedInUser } from 'types'
import { PortalMessageContextManager } from 'ui/src/components/message/portal-message/context'
import { AuthModalContextManager } from './auth-modal-context'
import EditManager from './edit-manager'
import { FavoritesManager } from './favorites-manager'
import FlashMessage from './flash-message'
import SearchTypeContextManager from './search-type'
import SearchbarContextManager from './searchbar-manager'
import UAParserContext, { UAParserProps } from './ua-parser'
import UserContext, { UserContextProps } from './user-context'

type Props = { children: ReactNode } & UserContextProps & UAParserProps
const ContextManager = ({ children, user: userSSR, deviceType, isBot }: Props) => {
    // user should come from ssr if there is a lot of page layout that depends on it
    // otherwise fetch clientside
    const [user, setUser] = useState<ILoggedInUser | undefined | null>(userSSR)
    const refreshUser = useCallback(async () => {
        try {
            const nextUser = await getUserOnClient()
            const normalizedUser = nextUser || null
            setUser(normalizedUser)
            return normalizedUser
        } catch (error) {
            setUser(null)
            return null
        }
    }, [])
    const setAuthenticatedUser = useCallback((nextUser: ILoggedInUser | null | undefined) => {
        setUser(nextUser || null)
    }, [])

    useEffect(() => {
        if (user === undefined) {
            void refreshUser()
        }
    }, [refreshUser, user])
    return (
        <UAParserContext.Provider value={{ deviceType, isBot }}>
            <UserContext.Provider
                value={{
                    user,
                    hasFetchedUser: user !== undefined,
                    refreshUser,
                    setAuthenticatedUser,
                }}
            >
                <SWRConfig
                    value={{
                        fetcher: (resource, init) => fetch(resource, init).then((res) => res.json()),
                        revalidateOnFocus: false,
                    }}
                >
                    <SearchTypeContextManager>
                        <PortalMessageContextManager>
                            <FlashMessage>
                                <AuthModalContextManager>
                                    <EditManager>
                                        <FavoritesManager>
                                            <SearchbarContextManager>
                                                {children}
                                                <AuthModal />
                                            </SearchbarContextManager>
                                        </FavoritesManager>
                                    </EditManager>
                                </AuthModalContextManager>
                            </FlashMessage>
                        </PortalMessageContextManager>
                    </SearchTypeContextManager>
                </SWRConfig>
            </UserContext.Provider>
        </UAParserContext.Provider>
    )
}

export default ContextManager
